/**
 * useAudioLevel hook - Real-time microphone volume level for visual feedback.
 *
 * Uses Web Audio API (getUserMedia + AnalyserNode) to read audio levels
 * without recording. Designed for the microphone test screen — gives users
 * visual confirmation their mic is picking up sound before the conversation.
 */

import { useState, useCallback, useRef, useEffect } from 'react'

export interface UseAudioLevelReturn {
  /** Normalised volume level 0–1 (RMS amplitude) */
  level: number
  /** Per-bar amplitude values (0–1) sampled from the real waveform */
  bars: readonly number[]
  /** Whether the mic stream is active and analysing */
  isActive: boolean
  /** Whether getUserMedia is supported in this browser */
  isSupported: boolean
  /** User-friendly error message, or null */
  error: string | null
  /** Request mic permission and start reading levels */
  start: () => Promise<void>
  /** Stop the mic stream and release resources. Returns a promise that resolves once the stream is fully released. */
  stop: () => Promise<void>
}

/** Number of bars exposed for waveform visualization */
const BAR_COUNT = 7

/** Empty bars array — shared reference to avoid re-renders */
const EMPTY_BARS: readonly number[] = Array.from({ length: BAR_COUNT }, () => 0)

// Minimum interval between state updates (ms) — ~15 fps
const UPDATE_INTERVAL_MS = 66

/**
 * Check whether getUserMedia is available.
 */
function isGetUserMediaSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'mediaDevices' in navigator &&
    'getUserMedia' in navigator.mediaDevices
  )
}

/**
 * Get the AudioContext constructor, handling Safari prefix.
 */
function getAudioContextClass(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  return window.AudioContext || (window as any).webkitAudioContext || null
}

export function useAudioLevel(): UseAudioLevelReturn {
  const [level, setLevel] = useState(0)
  const [bars, setBars] = useState<readonly number[]>(EMPTY_BARS)
  const [isActive, setIsActive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSupported] = useState(isGetUserMediaSupported)

  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const animFrameRef = useRef<number>(0)
  const lastUpdateRef = useRef(0)
  const cancelledRef = useRef(false)

  const cleanup = useCallback(async () => {
    cancelledRef.current = true
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current)
      animFrameRef.current = 0
    }
    // Stop all mic tracks first so the browser releases the mic
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    // Close AudioContext and await completion so the browser fully releases
    // internal audio resources before the next getUserMedia call
    if (audioContextRef.current) {
      try {
        await audioContextRef.current.close()
      } catch {
        // Ignore — context may already be closed
      }
      audioContextRef.current = null
    }
  }, [])

  const stop = useCallback(async () => {
    await cleanup()
    setIsActive(false)
    setLevel(0)
    setBars(EMPTY_BARS)
  }, [cleanup])

  const start = useCallback(async () => {
    setError(null)
    cancelledRef.current = false

    const AudioContextClass = getAudioContextClass()
    if (!isGetUserMediaSupported() || !AudioContextClass) {
      setError('Microphone testing is not supported in this browser.')
      return
    }

    // Clean up any previous session
    await cleanup()
    cancelledRef.current = false

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      })

      // Guard: component may have unmounted while awaiting permission
      if (cancelledRef.current) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      streamRef.current = stream

      const audioContext = new AudioContextClass()
      audioContextRef.current = audioContext

      // iOS Safari: AudioContext may start suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume()
      }

      // Guard: component may have unmounted while awaiting resume
      if (cancelledRef.current) {
        await audioContext.close().catch(() => {})
        stream.getTracks().forEach((t) => t.stop())
        streamRef.current = null
        audioContextRef.current = null
        return
      }

      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)

      const dataArray = new Uint8Array(analyser.frequencyBinCount)

      setIsActive(true)

      // Animation loop — reads time-domain data and computes RMS + per-bar amplitudes
      const samplesPerBar = Math.floor(dataArray.length / BAR_COUNT)
      const totalProcessed = samplesPerBar * BAR_COUNT
      let prevBars: readonly number[] = EMPTY_BARS

      const tick = (timestamp: number) => {
        if (cancelledRef.current) return
        // Throttle state updates to ~15 fps
        if (timestamp - lastUpdateRef.current >= UPDATE_INTERVAL_MS) {
          lastUpdateRef.current = timestamp

          analyser.getByteTimeDomainData(dataArray)

          // Compute overall RMS and per-bar amplitudes in a single pass
          let sumSquares = 0
          const newBars: number[] = []

          for (let bar = 0; bar < BAR_COUNT; bar++) {
            const segmentStart = bar * samplesPerBar
            const segmentEnd = segmentStart + samplesPerBar
            let barMax = 0

            for (let i = segmentStart; i < segmentEnd; i++) {
              const deviation = ((dataArray[i] ?? 128) - 128) / 128
              sumSquares += deviation * deviation
              const abs = Math.abs(deviation)
              if (abs > barMax) barMax = abs
            }

            // Quantize each bar to 2 decimal places
            newBars.push(Math.round(Math.min(barMax, 1) * 100) / 100)
          }

          const rms = Math.sqrt(sumSquares / totalProcessed)
          setLevel(Math.min(Math.round(rms * 100) / 100, 1))

          // Only update bars state when values actually changed
          const changed = newBars.some((v, i) => v !== prevBars[i])
          if (changed) {
            prevBars = newBars
            setBars(newBars)
          }
        }

        animFrameRef.current = requestAnimationFrame(tick)
      }

      animFrameRef.current = requestAnimationFrame(tick)
    } catch (err) {
      if (cancelledRef.current) return

      console.error('[useAudioLevel] Failed to start:', err)

      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError(
            'Microphone access was denied. Please allow microphone access in your browser settings.'
          )
        } else if (err.name === 'NotFoundError') {
          setError('No microphone was found. Please connect a microphone and try again.')
        } else {
          setError(`Microphone error: ${err.message}`)
        }
      } else {
        setError('Failed to access the microphone.')
      }

      await cleanup()
    }
  }, [cleanup])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      void cleanup()
    }
  }, [cleanup])

  return { level, bars, isActive, isSupported, error, start, stop }
}
